import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Send, Plane, BedDouble, CalendarCheck, Ticket, Sparkles, Lock, ExternalLink, MapPin } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/context/AuthContext";
import { usePageMeta } from "@/lib/site";
import { yasminApi, YASMIN_EXAMPLES } from "@/lib/yasminApi";
import { gbp } from "@/lib/format";
import { AgentAvatar, YASMIN } from "@/components/agents/Agents";
import { api } from "@/lib/api";

const PLAN_ICON = { flights: Plane, hotel: BedDouble, booking: CalendarCheck, events: Ticket };

function Part({ p }) {
  const Icon = PLAN_ICON[p.kind] || Sparkles;
  return (
    <div className="rounded-xl border border-border bg-background p-3 text-sm" data-testid={`plan-part-${p.kind}`}>
      <p className="flex items-center gap-2 font-semibold"><Icon size={15} style={{ color: "var(--provider)" }} /> {p.title || p.label || p.kind}{p.date ? <span className="font-normal text-muted-foreground">· {p.date}</span> : null}</p>
      {p.kind === "flights" && (
        <ul className="mt-2 space-y-1.5">
          {(p.options || []).slice(0, 3).map((o) => (
            <li key={o.id} className="flex items-center justify-between gap-3 rounded-lg bg-muted/60 px-3 py-2">
              <span className="min-w-0 truncate">{o.airline} · {o.stops === 0 ? "direct" : `${o.stops} stop${o.stops > 1 ? "s" : ""}`} · {(o.slices || []).map((s) => `${s.origin}→${s.destination}`).join(" / ")}</span>
              <span className="font-mono-num font-semibold">{gbp(o.price)}</span>
            </li>
          ))}
          {p.links && Object.values(p.links).map((l) => (
            <li key={l.url}><a href={l.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline">{l.label} <ExternalLink size={11} /></a></li>
          ))}
          {p.price_note && <li className="text-xs text-muted-foreground">{p.price_note}</li>}
        </ul>
      )}
      {p.kind === "hotel" && (
        <ul className="mt-2 space-y-1.5">
          {(p.hotels || []).slice(0, 3).map((h) => (
            <li key={h.id} className="flex items-center justify-between gap-3 rounded-lg bg-muted/60 px-3 py-2">
              <span className="min-w-0 truncate">{h.name}{h.distance_km != null ? ` · ${h.distance_km} km` : ""}{h.stars ? ` · ${h.stars}★` : ""}</span>
              <span className="flex items-center gap-2">{h.price != null && <span className="font-mono-num font-semibold">{gbp(h.price)}</span>}<a href={h.partner_url} target="_blank" rel="noopener noreferrer" className="text-xs font-medium text-primary hover:underline">View</a></span>
            </li>
          ))}
          {p.affiliate && <li className="text-xs text-muted-foreground">Fable+ may earn a commission on hotel links — it never changes your price.</li>}
        </ul>
      )}
      {p.kind === "booking" && (
        <div className="mt-2 rounded-lg bg-muted/60 px-3 py-2">
          {p.listing ? (
            <Link to={`/listing/${p.listing.id}`} className="font-medium hover:underline">{p.listing.title}</Link>
          ) : <span>{p.label}</span>}
          <p className="text-xs text-muted-foreground">{[p.service?.name, p.time ? `${p.date || ""} ${p.time}`.trim() : null, p.quote ? gbp(p.quote.price) : null].filter(Boolean).join(" · ")}{p.booked ? " · booked" : ""}</p>
          {p.listing && !p.booked && <Link to={`/listing/${p.listing.id}`} className="mt-1 inline-block text-xs font-medium text-primary hover:underline">Book on the listing</Link>}
        </div>
      )}
      {p.kind === "events" && (
        <ul className="mt-2 space-y-1.5">
          {(p.events || []).slice(0, 4).map((e) => (
            <li key={e.id} className="flex items-center justify-between gap-3 rounded-lg bg-muted/60 px-3 py-2">
              <Link to={`/events/${e.id}`} className="min-w-0 truncate font-medium hover:underline">{e.title}</Link>
              <span className="shrink-0 text-xs text-muted-foreground">{e.free ? "Free" : e.from_p != null ? `from £${(e.from_p / 100).toFixed(2)}` : ""}</span>
            </li>
          ))}
        </ul>
      )}
      {p.kind === "events" && p.local && ((p.local.holidays || []).length > 0 || (p.local.festivals || []).length > 0) && (
        <div className="mt-2 rounded-lg border border-border/70 px-3 py-2" data-testid="yasmin-local">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Also in {p.local.city}</p>
          <ul className="mt-1 space-y-1 text-sm">
            {(p.local.holidays || []).map((h) => <li key={h.date + h.name}><span className="font-medium">{new Date(h.date + "T12:00:00").toLocaleDateString("en-GB", { day: "numeric", month: "short" })}</span> · {h.name} <span className="text-xs text-muted-foreground">(public holiday)</span></li>)}
            {(p.local.festivals || []).slice(0, 5).map((f) => <li key={f.name}><a href={f.url} target="_blank" rel="noopener noreferrer" className="font-medium hover:underline">{f.name}</a>{f.month ? <span className="text-xs text-muted-foreground"> · usually {f.month.charAt(0).toUpperCase() + f.month.slice(1)}</span> : null}{f.about ? <span className="block text-xs text-muted-foreground">{f.about}</span> : null}</li>)}
          </ul>
          <p className="mt-1 text-[10px] text-muted-foreground">Open data from Nager.Date and Wikidata — check dates with the organiser.</p>
        </div>
      )}
      {p.note && <p className="mt-2 text-xs text-muted-foreground">{p.note}</p>}
    </div>
  );
}

export function Itinerary({ plan, onSave }) {
  if (!plan) return null;
  return (
    <div className="mt-2 space-y-2" data-testid="itinerary">
      <p className="text-sm font-semibold">{plan.title}</p>
      {(plan.parts || []).map((p) => <Part key={p.id} p={p} />)}
      {plan.budget && plan.budget.total != null && <p className="text-xs text-muted-foreground">Total so far {gbp(plan.budget.total)}{plan.budget.amount ? ` of ${gbp(plan.budget.amount)}` : ""}.</p>}
      {!plan.saved && onSave && <Button size="sm" variant="outline" className="rounded-full" onClick={() => onSave(plan.id)}>Save this plan</Button>}
      {(plan.parts || []).some((p) => p.kind === "flight") && <Button size="sm" variant="ghost" className="rounded-full" onClick={() => api("/yasmin/flight-watches", { method: "POST", body: { plan_id: plan.id } }).then(() => toast.success("Yasmin will tell you if the price drops")).catch((e) => toast.error(e.message))}>Watch flight price</Button>}
    </div>
  );
}

function DayPlan({ plan }) {
  if (!plan) return null;
  const items = plan.items || plan.slots || [];
  return (
    <div className="mt-2 space-y-1.5" data-testid="day-plan">
      {items.map((it, i) => (
        <div key={i} className="flex items-start gap-3 rounded-lg bg-muted/60 px-3 py-2 text-sm">
          <span className="w-14 shrink-0 font-mono-num text-xs text-muted-foreground">{it.time || ""}</span>
          <span className="min-w-0 flex-1">{it.listing_id ? <Link to={`/listing/${it.listing_id}`} className="font-medium hover:underline">{it.title}</Link> : <span className="font-medium">{it.title}</span>}{it.note ? <span className="block text-xs text-muted-foreground">{it.note}</span> : null}</span>
        </div>
      ))}
    </div>
  );
}

function Suggestions({ items }) {
  if (!items?.length) return null;
  return (
    <div className="mt-2 grid gap-2 sm:grid-cols-2">
      {items.slice(0, 4).map((c) => (
        <Link key={c.id} to={`/listing/${c.id}`} className="flex gap-3 rounded-xl border border-border bg-background p-2 hover:bg-muted/60">
          <div className="h-14 w-14 shrink-0 overflow-hidden rounded-lg bg-muted">{c.image_url && <img src={c.image_url} alt="" className="h-full w-full object-cover" />}</div>
          <div className="min-w-0 text-sm">
            <p className="truncate font-semibold">{c.title}</p>
            <p className="truncate text-xs text-muted-foreground">{[c.provider_name, c.location].filter(Boolean).join(" · ")}</p>
            <p className="text-xs">{c.price_from != null ? `from ${gbp(c.price_from)}` : c.price != null ? gbp(c.price) : ""}{c.rating ? ` · ★ ${c.rating}` : ""}</p>
          </div>
        </Link>
      ))}
    </div>
  );
}

function Unlock({ access, onUnlock, busy }) {
  return (
    <div className="mx-auto max-w-2xl px-4 py-12 sm:px-6" data-testid="yasmin-locked">
      <div className="rounded-3xl border border-border bg-card p-6 sm:p-8">
        <div className="flex items-center gap-3">
          <Link to="/agents/yasmin" title="About Yasmin" data-testid="agent-avatar-link-yasmin"><AgentAvatar a={YASMIN} size={56} /></Link>
          <div>
            <h1 className="font-serif-display text-2xl font-bold">Yasmin</h1>
            <p className="text-sm text-muted-foreground">Your Fable + AI assistant · £{Number(access?.price || 3.7).toFixed(2)} a month</p>
          </div>
        </div>
        <ul className="mt-6 space-y-3">
          {(access?.preview || []).map((p) => (
            <li key={p.title} className="rounded-xl bg-muted/60 p-3"><p className="font-semibold">{p.title}</p><p className="text-sm text-muted-foreground">{p.body}</p></li>
          ))}
        </ul>
        {access?.example && (
          <div className="mt-5 rounded-2xl border border-border p-4">
            <p className="text-xs font-semibold text-muted-foreground">You say</p>
            <p className="mt-1 text-sm">{access.example.ask}</p>
            <p className="mt-3 text-xs font-semibold text-muted-foreground">Yasmin comes back with</p>
            <ul className="mt-1 space-y-1 text-sm">{access.example.lines.map((l) => <li key={l}>{l}</li>)}</ul>
          </div>
        )}
        {access?.teen ? (
          <div className="mt-6 text-sm text-muted-foreground"><p>Yasmin for teens is unlocked by a parent or guardian linked to your account.</p><Button size="sm" variant="outline" className="mt-2 rounded-full" onClick={() => api("/family/yasmin-request", { method: "POST" }).then((r) => toast.success(r && r.sent ? "We asked your parent to unlock Yasmin" : "Link a parent in Family first")).catch((e) => toast.error(e.message))}>Ask a parent to unlock Yasmin</Button></div>
        ) : (
          <>
            <Button className="mt-6 w-full rounded-full" onClick={onUnlock} disabled={busy} data-testid="yasmin-unlock"><Lock size={15} className="mr-1.5" /> {busy ? "One moment…" : `Unlock Yasmin · £${Number(access?.price || 3.7).toFixed(2)}/month`}</Button>
            <p className="mt-2 text-center text-[11px] text-muted-foreground">Cancel any time. Test payments · no real card charges.</p>
          </>
        )}
      </div>
    </div>
  );
}

export default function YasminChat() {
  usePageMeta("Yasmin", "Chat with Yasmin, the Fable + AI assistant: whole trips, bookings with your regular providers and a plan for your day.");
  const { user, bootstrapped } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [access, setAccess] = useState(undefined);
  const [busy, setBusy] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [location, setLocation] = useState(null);
  const endRef = useRef(null);
  // ?q= from the Travel hub (or anywhere): send it once Yasmin is unlocked, otherwise leave it in the box.
  const sentQ = useRef(null);
  useEffect(() => {
    const q = params.get("q");
    if (!q || !user || access === undefined || sentQ.current === q) return;
    sentQ.current = q;
    const n = new URLSearchParams(params); n.delete("q"); setParams(n, { replace: true });
    if (access?.active) send(q); else { setText(q); try { sessionStorage.setItem("yasmin_pending_q", q); } catch { /* private mode */ } }
  }, [params, user?.id, access]); // eslint-disable-line react-hooks/exhaustive-deps
  // Back from unlocking Yasmin: put the trip they asked for back in the box.
  useEffect(() => {
    if (!access?.active) return;
    try { const q = sessionStorage.getItem("yasmin_pending_q"); if (q) { sessionStorage.removeItem("yasmin_pending_q"); setText((t) => t || q); } } catch { /* private mode */ }
  }, [access?.active]);

  useEffect(() => {
    if (bootstrapped && !user) navigate("/login", { state: { from: "/yasmin/chat" + window.location.search } });
  }, [bootstrapped, user, navigate]);

  const loadAccess = () => yasminApi.access().then(setAccess).catch(() => setAccess(null));
  useEffect(() => { if (user) loadAccess(); }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // back from Stripe
  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid || !user) return undefined;
    let tries = 0;
    let alive = true;
    const poll = async () => {
      try {
        const a = await yasminApi.checkoutStatus(sid);
        if (!alive) return;
        if (a.active) { setAccess(a); toast.success("Yasmin is unlocked."); const n = new URLSearchParams(params); n.delete("session_id"); setParams(n, { replace: true }); return; }
      } catch { /* retry */ }
      if (++tries < 12 && alive) setTimeout(poll, 2000);
    };
    poll();
    return () => { alive = false; };
  }, [params, user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }); }, [msgs, sending]);

  const unlock = async () => {
    setBusy(true);
    try {
      const r = await yasminApi.checkout(`${window.location.origin}`);
      if (r.url) { window.location.assign(r.url); return; }
      toast.error("Couldn't start checkout.");
    } catch (e) {
      toast.error(e.message || "Couldn't start checkout.");
    } finally {
      setBusy(false);
    }
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition((p) => { setLocation({ lat: p.coords.latitude, lng: p.coords.longitude, label: "My location" }); toast.success("Location set"); }, () => toast("Couldn't get your location."), { timeout: 8000 });
  };

  const send = async (raw) => {
    const message = (raw ?? text).trim();
    if (!message || sending) return;
    setText("");
    setMsgs((m) => [...m, { role: "user", text: message }]);
    setSending(true);
    try {
      const r = await yasminApi.chat(message, sessionId, location);
      if (r.session_id) setSessionId(r.session_id);
      setMsgs((m) => [...m, { role: "yasmin", text: r.reply, suggestions: r.suggestions, itinerary: r.itinerary, plan: r.plan, question: r.question, askLocation: r.ask_location, escalate: r.escalate }]);
    } catch (e) {
      if (e.status === 402) { loadAccess(); }
      setMsgs((m) => [...m, { role: "yasmin", text: e.message || "Something went wrong. Please try again.", error: true }]);
    } finally {
      setSending(false);
    }
  };
  const savePlan = async (pid) => {
    try { await yasminApi.save(pid); toast.success("Plan saved — find it under Travel › My trips."); setMsgs((m) => m.map((x) => (x.itinerary?.id === pid ? { ...x, itinerary: { ...x.itinerary, saved: true } } : x))); } catch (e) { toast.error(e.message || "Couldn't save the plan."); }
  };

  if (!user || access === undefined) {
    return <div className="mx-auto max-w-2xl px-4 py-12 sm:px-6"><Skeleton className="h-64 rounded-3xl" /></div>;
  }
  if (access === null) return <p className="mx-auto max-w-2xl px-4 py-20 text-center text-muted-foreground">Couldn't reach Yasmin right now. Please try again shortly.</p>;
  if (!access.active) return <Unlock access={access} onUnlock={unlock} busy={busy} />;


  return (
    <div className="mx-auto flex h-[calc(100vh-4rem)] max-w-3xl flex-col px-4 sm:px-6" data-testid="yasmin-chat">
      <header className="flex items-center gap-3 border-b border-border py-4">
        <AgentAvatar a={YASMIN} size={44} />
        <div className="min-w-0 flex-1">
          <h1 className="font-bold leading-tight">Yasmin</h1>
          <p className="text-xs text-emerald-600">Online · {access.staff ? "staff" : "member"}</p>
        </div>
        <button type="button" onClick={useMyLocation} className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-medium ${location ? "border-transparent bg-foreground text-background" : "border-border hover:bg-muted"}`} data-testid="yasmin-location"><MapPin size={12} /> {location ? "Location set" : "Use my location"}</button>
        <Link to="/yasmin" className="text-xs text-muted-foreground hover:underline">About</Link>
      </header>

      <div className="flex-1 space-y-4 overflow-y-auto py-5" data-testid="yasmin-thread">
        {msgs.length === 0 && (
          <div>
            <p className="text-sm text-muted-foreground">Tell Yasmin what you want to do — a trip, a booking with your regular, or a plan for the day. Try one:</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {YASMIN_EXAMPLES.map((ex) => (
                <button key={ex} type="button" onClick={() => send(ex)} className="rounded-full border border-border bg-card px-3 py-1.5 text-left text-sm hover:bg-muted" data-testid="yasmin-example">{ex}</button>
              ))}
            </div>
          </div>
        )}
        {msgs.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm ${m.role === "user" ? "rounded-br-md text-white" : `rounded-bl-md bg-card border border-border ${m.error ? "text-destructive" : ""}`}`} style={m.role === "user" ? { background: "var(--fable)" } : undefined}>
              <p className="whitespace-pre-wrap">{m.text}</p>
              {m.question && <p className="mt-2 font-medium">{m.question}</p>}
              {m.askLocation && <button type="button" onClick={useMyLocation} className="mt-2 inline-flex items-center gap-1 rounded-full border border-border px-3 py-1 text-xs font-medium hover:bg-muted"><MapPin size={12} /> Share my location</button>}
              <Suggestions items={m.suggestions} />
              <Itinerary plan={m.itinerary} onSave={savePlan} />
              <DayPlan plan={m.plan} />
              {m.escalate && <Link to="/contact" className="mt-2 inline-block text-xs font-medium text-primary hover:underline">This needs a real person — contact support</Link>}
            </div>
          </div>
        ))}
        {sending && <div className="flex justify-start"><div className="rounded-2xl rounded-bl-md border border-border bg-card px-4 py-3 text-sm text-muted-foreground">Yasmin is thinking…</div></div>}
        <div ref={endRef} />
      </div>

      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-end gap-2 border-t border-border py-3">
        <textarea value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} rows={1} maxLength={1000} placeholder="Message Yasmin…" data-testid="yasmin-input" className="max-h-32 flex-1 resize-none rounded-2xl border border-border bg-card px-4 py-2.5 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20" />
        <Button type="submit" className="rounded-full" disabled={!text.trim() || sending} aria-label="Send" data-testid="yasmin-send"><Send size={16} /></Button>
      </form>
    </div>
  );
}
